/**
 * AgentMessageList — Streaming message display with tool status indicators
 */
import { useEffect, useRef } from 'react'
import { Sparkles } from 'lucide-react'
import type { AgentMessage } from '@/lib/api/agent'

interface AgentMessageListProps {
  messages: AgentMessage[]
  streamingText?: string
  toolStatus?: { name: string; status: 'started' | 'completed' | 'failed' }[]
  welcomeMessage?: boolean
}

const WELCOME_TEXT = `Hey there! 👋 I'm your Servament AI assistant.

I can help you with things like:
• **Looking up customers, jobs, and cases**
• **Creating bookings and scheduling appointments**
• **Checking dispatch and technician availability**
• **Pulling up invoices and payment status**
• **Managing equipment and part requests**

Just type what you need — I'll take it from there.`

export function AgentMessageList({ messages, streamingText, toolStatus, welcomeMessage }: AgentMessageListProps) {
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText])

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {welcomeMessage && (
        <div className="flex justify-start">
          <div className="max-w-[85%] space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <Sparkles className="h-5 w-5" />
              <span className="text-sm font-semibold">Servament AI</span>
            </div>
            <div className="rounded-lg bg-muted px-4 py-3 text-sm text-foreground leading-relaxed whitespace-pre-line">
              {WELCOME_TEXT.split('**').map((part, i) =>
                i % 2 === 1 ? <span key={i} className="font-medium">{part}</span> : part
              )}
            </div>
          </div>
        </div>
      )}

      {messages.map((msg, i) => (
        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
            msg.role === 'user'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground'
          }`}>
            {msg.content}
          </div>
        </div>
      ))}

      {toolStatus?.map((tool, i) => (
        <div key={`tool-${i}`} className="flex justify-start">
          <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground">
            {tool.status === 'started' && <span className="animate-spin">⏳</span>}
            {tool.status === 'completed' && <span>✅</span>}
            {tool.status === 'failed' && <span>❌</span>}
            <span>{tool.name}</span>
          </div>
        </div>
      ))}

      {streamingText && (
        <div className="flex justify-start">
          <div className="max-w-[80%] rounded-lg bg-muted px-3 py-2 text-sm text-foreground">
            {streamingText}
            <span className="animate-pulse">▊</span>
          </div>
        </div>
      )}

      <div ref={endRef} />
    </div>
  )
}
